import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, BarElement, PointElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { statsAPI, incomeAPI, expensesAPI, categoriesAPI } from '../api';
import useAuthStore from '../store/auth';
import { formatAmount } from '../utils/currency';

ChartJS.register(CategoryScale, LinearScale, LineElement, BarElement, PointElement, Title, Tooltip, Legend, Filler);

// ─── Export PDF (utilise window.print avec un style dedie) ───
function exportPDF(data) {
  const { user, trend, incomes, expenses, cats, period, currency } = data;
  const fmt = n => formatAmount(n, currency);
  const totalIncome  = incomes.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const totalExpense = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const bycat = cats.map(c => ({
    ...c,
    spent: expenses.filter(e => e.category_id === c.id).reduce((s, e) => s + parseFloat(e.amount || 0), 0),
  })).filter(c => c.spent > 0).sort((a, b) => b.spent - a.spent);

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Rapport BudgetFlow - ${period}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, sans-serif; color: #1a1a2e; background: #fff; padding: 40px; }
  .header { background: linear-gradient(135deg, #7c6af7, #f7c26a); padding: 32px; border-radius: 12px; margin-bottom: 32px; color: white; display: flex; justify-content: space-between; align-items: center; }
  .header h1 { font-size: 28px; font-weight: 800; }
  .header p  { opacity: 0.85; font-size: 14px; margin-top: 4px; }
  .header .meta { text-align: right; font-size: 13px; opacity: 0.85; }
  .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 32px; }
  .stat-card { border: 1px solid #e0e0f0; border-radius: 12px; padding: 20px; }
  .stat-card .label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #666; margin-bottom: 8px; }
  .stat-card .value { font-size: 24px; font-weight: 800; }
  .green  { color: #00aa66; } .red { color: #e03030; } .purple { color: #7c6af7; }
  .section { margin-bottom: 32px; }
  .section h2 { font-size: 16px; font-weight: 700; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #f0f0f8; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { background: #f8f8ff; padding: 10px 12px; text-align: left; font-weight: 600; color: #444; border-bottom: 2px solid #e0e0f0; }
  td { padding: 10px 12px; border-bottom: 1px solid #f0f0f8; }
  tr:hover td { background: #fafafa; }
  .bar-wrap { display: flex; align-items: center; gap: 8px; }
  .bar { height: 10px; border-radius: 5px; }
  .footer { text-align: center; font-size: 12px; color: #999; margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; }
  @media print { body { padding: 20px; } .no-print { display: none; } }
</style></head><body>
<div class="header">
  <div>
    <h1>BudgetFlow</h1>
    <p>Rapport financier - ${period}</p>
  </div>
  <div class="meta">
    <div>${user?.name || ''}</div>
    <div>${new Date().toLocaleDateString('fr-FR')}</div>
  </div>
</div>

<div class="stats">
  <div class="stat-card"><div class="label">Revenus totaux</div><div class="value green">${fmt(totalIncome)}</div></div>
  <div class="stat-card"><div class="label">Depenses totales</div><div class="value red">${fmt(totalExpense)}</div></div>
  <div class="stat-card"><div class="label">Solde net</div><div class="value purple">${fmt(totalIncome - totalExpense)}</div></div>
</div>

<div class="section">
  <h2>Depenses par categorie</h2>
  <table>
    <tr><th>Categorie</th><th>Montant</th><th>% du total</th><th>Repartition</th></tr>
    ${bycat.map(c => {
      const pct = totalExpense > 0 ? (c.spent / totalExpense * 100).toFixed(1) : 0;
      return '<tr><td>' + c.icon + ' ' + c.name + '</td><td><strong>' + fmt(c.spent) + '</strong></td><td>' + pct + '%</td><td><div class="bar-wrap"><div class="bar" style="width:' + pct + '%;background:' + (c.color || '#7c6af7') + '"></div></div></td></tr>';
    }).join('')}
  </table>
</div>

<div class="section">
  <h2>Dernieres depenses</h2>
  <table>
    <tr><th>Date</th><th>Categorie</th><th>Description</th><th>Montant</th></tr>
    ${expenses.slice(0, 20).map(e => '<tr><td>' + new Date(e.date).toLocaleDateString('fr-FR') + '</td><td>' + (e.category_icon || '') + ' ' + (e.category_name || '') + '</td><td>' + (e.description || '-') + '</td><td style="color:#e03030;font-weight:600">-' + fmt(e.amount) + '</td></tr>').join('')}
  </table>
</div>

<div class="section">
  <h2>Derniers revenus</h2>
  <table>
    <tr><th>Date</th><th>Source</th><th>Description</th><th>Montant</th></tr>
    ${incomes.slice(0, 15).map(i => '<tr><td>' + new Date(i.date).toLocaleDateString('fr-FR') + '</td><td>' + (i.source || '') + '</td><td>' + (i.description || '-') + '</td><td style="color:#00aa66;font-weight:600">+' + fmt(i.amount) + '</td></tr>').join('')}
  </table>
</div>

<div class="footer">Rapport genere par BudgetFlow le ${new Date().toLocaleDateString('fr-FR')} - Confidentiel</div>
</body></html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  win.onload = () => { win.focus(); win.print(); };
}

// ─── Export Excel (CSV) ───────────────────────────────────────
function exportExcel(data) {
  const { incomes, expenses, trend, period, currency } = data;
  const fmt = n => formatAmount(n, currency);

  const NL = "\n";
  let csv = "BudgetFlow - Rapport " + period + NL + NL;

  csv += "REVENUS" + NL;
  csv += "Date,Source,Description,Montant" + NL;
  incomes.forEach(i => {
    csv += [new Date(i.date).toLocaleDateString("fr-FR"), i.source, i.description || "", parseFloat(i.amount).toFixed(2)].join(",") + NL;
  });

  csv += NL + "DEPENSES" + NL;
  csv += "Date,Categorie,Description,Montant" + NL;
  expenses.forEach(e => {
    csv += [new Date(e.date).toLocaleDateString("fr-FR"), e.category_name || "", e.description || "", parseFloat(e.amount).toFixed(2)].join(",") + NL;
  });

  csv += NL + "TENDANCE MENSUELLE" + NL;
  csv += "Mois,Revenus,Depenses,Solde" + NL;
  (trend || []).forEach(m => {
    csv += [m.month, parseFloat(m.income).toFixed(2), parseFloat(m.expense).toFixed(2), (parseFloat(m.income) - parseFloat(m.expense)).toFixed(2)].join(",") + NL;
  });

  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "budgetflow-rapport-" + period + ".csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const { t } = useTranslation();
  const { hasAccess, user } = useAuthStore();
  const currency = user?.currency || 'EUR';
  const fmt = n => formatAmount(n, currency);
  const navigate = useNavigate();
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const { data: trend }    = useQuery({ queryKey:['trend'],    queryFn:() => statsAPI.trend().then(r => r.data),            enabled:hasAccess() });
  const { data: incomes }  = useQuery({ queryKey:['income-all'],   queryFn:() => incomeAPI.list({ month:period }).then(r => r.data),   enabled:hasAccess() });
  const { data: expenses } = useQuery({ queryKey:['expense-all'],  queryFn:() => expensesAPI.list({ month:period }).then(r => r.data), enabled:hasAccess() });
  const { data: cats }     = useQuery({ queryKey:['categories'],   queryFn:() => categoriesAPI.list().then(r => r.data),               enabled:hasAccess() });

  if (!hasAccess()) return (
    <div style={{ textAlign:'center', padding:'80px 20px' }}>
      <div style={{ fontSize:64, marginBottom:16 }}>⏰</div>
      <h2 style={{ fontFamily:'Syne,sans-serif', marginBottom:12 }}>{t('trial_expired')}</h2>
      <p style={{ color:'var(--text2)', marginBottom:24 }}>{t('upgrade_message')}</p>
      <button onClick={() => navigate('/pricing')} style={{ padding:'14px 32px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:12, fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:16, cursor:'pointer' }}>
        {t('upgrade')}
      </button>
    </div>
  );

  const totalIncome  = (trend || []).reduce((s, m) => s + parseFloat(m.income  || 0), 0);
  const totalExpense = (trend || []).reduce((s, m) => s + parseFloat(m.expense || 0), 0);
  const savingsRate  = totalIncome > 0 ? ((totalIncome - totalExpense) / totalIncome * 100).toFixed(1) : 0;

  const bycat = (cats || []).map(c => ({
    ...c,
    spent: (expenses || []).filter(e => e.category_id === c.id).reduce((s, e) => s + parseFloat(e.amount || 0), 0),
  })).filter(c => c.spent > 0).sort((a, b) => b.spent - a.spent);

  const handleExport = (type) => {
    setExporting(true);
    setShowExportModal(false);
    const exportData = { user, trend: trend || [], incomes: incomes || [], expenses: expenses || [], cats: cats || [], period, currency };
    setTimeout(() => {
      if (type === 'pdf')   exportPDF(exportData);
      if (type === 'excel') exportExcel(exportData);
      setExporting(false);
    }, 100);
  };

  const chartOpts = {
    responsive: true,
    plugins: { legend: { labels: { color: '#9090b0' } } },
    scales: { x: { grid: { color: '#2a2a3d' }, ticks: { color: '#9090b0' } }, y: { grid: { color: '#2a2a3d' }, ticks: { color: '#9090b0', callback: v => fmt(v) } } },
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontFamily:'Syne,sans-serif', fontSize:24, fontWeight:700 }}>{t('reports_title')}</h1>
          <p style={{ color:'var(--text2)', fontSize:14, marginTop:4 }}>Analyse de vos finances</p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
            style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px', color:'var(--text)', fontSize:14, outline:'none' }} />
          <button onClick={() => setShowExportModal(true)} disabled={exporting}
            style={{ padding:'10px 20px', background:'linear-gradient(135deg,#7c6af7,#f7c26a)', color:'#fff', border:'none', borderRadius:10, fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
            {exporting ? '...' : '📤 Exporter'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:16, marginBottom:24 }}>
        {[
          { label:t('total_income'),  value:fmt(totalIncome),              color:'var(--accent3)', icon:'📈' },
          { label:t('total_expense'), value:fmt(totalExpense),             color:'var(--danger)',   icon:'📉' },
          { label:t('balance'),       value:fmt(totalIncome-totalExpense), color:'var(--accent)',   icon:'💰' },
          { label:t('savings_pct'),   value:savingsRate + '%',             color:'var(--accent2)',  icon:'🎯' },
        ].map(s => (
          <div key={s.label} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
            <div style={{ fontSize:20, marginBottom:8 }}>{s.icon}</div>
            <div style={{ fontSize:11, color:'var(--text2)', marginBottom:6 }}>{s.label}</div>
            <div style={{ fontFamily:'Syne,sans-serif', fontSize:20, fontWeight:700, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Graphique tendance */}
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:16, padding:24, marginBottom:24 }}>
        <h3 style={{ fontFamily:'Syne,sans-serif', fontWeight:700, marginBottom:20 }}>{t('trend_6m')}</h3>
        {trend && trend.length > 0 ? (
          <Line data={{
            labels: trend.map(m => m.month),
            datasets: [
              { label:t('income'),   data:trend.map(m => m.income),  borderColor:'#6af7b8', backgroundColor:'#6af7b820', tension:0.4, fill:true, pointBackgroundColor:'#6af7b8' },
              { label:t('expenses'), data:trend.map(m => m.expense), borderColor:'#f76a6a', backgroundColor:'#f76a6a20', tension:0.4, fill:true, pointBackgroundColor:'#f76a6a' },
            ],
          }} options={chartOpts} />
        ) : <p style={{ color:'var(--text2)', textAlign:'center', padding:40 }}>{t('no_data')}</p>}
      </div>

      {/* Depenses par categorie */}
      {bycat.length > 0 && (
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:16, padding:24, marginBottom:24 }}>
          <h3 style={{ fontFamily:'Syne,sans-serif', fontWeight:700, marginBottom:20 }}>Depenses par categorie — {period}</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
            <div>
              {bycat.map((c, i) => {
                const pct = (expenses || []).reduce((s, e) => s + parseFloat(e.amount || 0), 0);
                const p = pct > 0 ? (c.spent / pct * 100).toFixed(1) : 0;
                return (
                  <div key={c.id} style={{ marginBottom:14 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5, fontSize:14 }}>
                      <span>{c.icon} {c.name}</span>
                      <span style={{ fontWeight:600 }}>{fmt(c.spent)} <span style={{ color:'var(--text3)', fontWeight:400 }}>({p}%)</span></span>
                    </div>
                    <div style={{ height:8, background:'var(--surface2)', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:p + '%', background:c.color || '#7c6af7', borderRadius:4 }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div>
              <Bar data={{
                labels: bycat.map(c => c.icon + ' ' + c.name),
                datasets: [{ label:'Depenses', data:bycat.map(c => c.spent), backgroundColor:bycat.map(c => (c.color || '#7c6af7') + '99'), borderColor:bycat.map(c => c.color || '#7c6af7'), borderWidth:2, borderRadius:6 }],
              }} options={{ ...chartOpts, indexAxis:'y', plugins:{ legend:{ display:false } } }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal export */}
      {showExportModal && (
        <div style={{ position:'fixed', inset:0, background:'#00000090', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:20, padding:32, width:'100%', maxWidth:400 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <h2 style={{ fontFamily:'Syne,sans-serif', fontWeight:700 }}>Exporter le rapport</h2>
              <button onClick={() => setShowExportModal(false)} style={{ background:'none', border:'none', fontSize:20, color:'var(--text2)', cursor:'pointer' }}>×</button>
            </div>
            <p style={{ color:'var(--text2)', fontSize:14, marginBottom:24 }}>Choisissez le format d export pour la periode <strong>{period}</strong>.</p>
            <div style={{ display:'grid', gap:12 }}>
              <button onClick={() => handleExport('pdf')}
                style={{ padding:20, background:'var(--surface2)', border:'2px solid var(--border)', borderRadius:14, cursor:'pointer', display:'flex', alignItems:'center', gap:16, textAlign:'left' }}>
                <div style={{ fontSize:36 }}>📄</div>
                <div>
                  <div style={{ fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:16, marginBottom:4 }}>PDF</div>
                  <div style={{ fontSize:13, color:'var(--text2)' }}>Rapport complet imprimable avec graphiques et tableaux</div>
                </div>
              </button>
              <button onClick={() => handleExport('excel')}
                style={{ padding:20, background:'var(--surface2)', border:'2px solid var(--border)', borderRadius:14, cursor:'pointer', display:'flex', alignItems:'center', gap:16, textAlign:'left' }}>
                <div style={{ fontSize:36 }}>📊</div>
                <div>
                  <div style={{ fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:16, marginBottom:4 }}>Excel / CSV</div>
                  <div style={{ fontSize:13, color:'var(--text2)' }}>Donnees brutes compatibles Excel, Google Sheets</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}